import { FC, useMemo } from "react";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";
import { EntityNotesButton } from "../components/EntityNotesButton";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { faPeopleGroup, faRefresh, faServer } from "@fortawesome/free-solid-svg-icons";
import {
  useServers,
  useSites,
} from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { usePersistentState } from "@hostingbv/lib/src/util/usePersistentState";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";
import { IconButton } from "@hostingbv/lib/src/components/form/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HoverHint } from "@hostingbv/lib/src/components/content/HoverHint";

export const Servers: FC = () => {
  return (
    <ContentContainer>
      <PageIconHeader icon={faServer}>
        <Breadcrumbs crumbs={[]} currentPage={"Servers"} />
        <PageHeader>Servers</PageHeader>
      </PageIconHeader>
      <AddServerForm />
      <ServerOverview />
    </ContentContainer>
  );
};

const AddServerForm: FC = () => {
  return (
    <Callout>
      <SectionHeader>Request new server</SectionHeader>
      <Paragraph>
        Do you need a new server? Please contact us and we will deploy it for
        you.
      </Paragraph>
    </Callout>
  );
};

const ServerOverview: FC = () => {
  const navigate = useNavigate();
  const [serverType, setServerType] = usePersistentState<string>(
    "servers.filter",
    "All",
  );

  const {
    data: sites,
    isLoading: loadingSites,
    fetch: fetchSites,
  } = useSites();
  const {
    data: servers,
    isLoading: loadingServers,
    fetch: fetchServers,
  } = useServers();

  const reload = () => {
    fetchSites();
    fetchServers();
  };

  const siteCountLookupTable = useMemo(() => {
    return (sites ?? []).reduce((table, { serverId }) => {
      table.set(serverId, (table.get(serverId) ?? 0) + 1);
      return table;
    }, new Map<string, number>());
  }, [sites]);

  const mappedServers = useMemo(() => {
    const typeMap: Record<string, string> = {
      "Shared Hosting": "shared_hosting",
      "Virtual Server": "virtual_server",
      "Windhosting Server": "windhosting_server",
    };

    const targetType = typeMap[serverType];
    return (servers ?? [])
      .filter((s) => !targetType || s.serverType === targetType)
      .map((server) => ({
        ...server,
        nSites: siteCountLookupTable.get(server.id) ?? 0,
      }));
  }, [servers, serverType, siteCountLookupTable]);

  const { getRealtimeStatus } = useRealtimeDeployments()!;
  return (
    <div className={"my-8"}>
      <SectionHeader>Your servers</SectionHeader>
      <TabLayout
        tabNames={[
          "All",
          "Shared Hosting",
          "Virtual Server",
          "Windhosting Server",
        ]}
        activeTab={serverType}
        setActiveTab={setServerType}
      />
      <FilterableDataTable
        keyProperty={"id"}
        loading={loadingServers || loadingSites}
        data={mappedServers}
        searchableProperties={["displayName", (server) => server.customer?.displayName, "notes"]}
        actions={<IconButton icon={faRefresh} onClick={reload} type={"secondary"} size={"sm"} />}
        columns={[
                {
                  header: "Name",
                  property: "displayName",
                  transform: (name, row) => (
                    <div className={"flex items-center space-x-2"}>
                      {name} <EntityNotesButton allowNew={false} entity={row} />
                      {row.isShared ? (
                        <HoverHint hint={"Shared server"}>
                          <FontAwesomeIcon
                            icon={faPeopleGroup}
                            className={"ml-2"}
                          />
                        </HoverHint>
                      ) : null}
                    </div>
                  ),
                  sort: true,
                },
                {
                  header: "Customer",
                  property: "customer",
                  transform: (customer) => customer?.displayName ?? "n/a",
                  sort: true,
                },
                {
                  header: "Type",
                  property: "serverType",
                  transform: (value) =>
                    ({
                      shared_hosting: "Shared Hosting",
                      virtual_server: "Virtual Server",
                      windhosting_server: "Windhosting Server",
                    })[value],
                },
                {
                  header: "Sites",
                  property: "nSites",
                  transform: (nSites) => nSites ?? "n/a",
                },
                {
                  header: "Deployment status",
                  property: "deploymentStatus",
                  transform: (status, row) => (
                    <DeploymentStatusLabel
                      status={getRealtimeStatus(row.id, status)}
                    />
                  ),
                },
        ]}
        onClickRow={(row, e) => navigateRow(navigate, `/servers/${row.id}`, e)}
      />
    </div>
  );
};
